import { useCallback } from 'react';
import { useMetricFetcher } from './useOptimizeUtil';
import { getMetricForEvent, PrimaryValue, ValueType } from '@fullstory/signal-studio-context';
import { DependentEventFilter } from '@fullstory/proto/fs/api/segment';
import { usePages } from '@fullstory/pages-shared';
import { useNamedElements } from './useNamedElements';
import { PageRefValueType } from '@fullstory/search-types';

type PreviewType = 'page' | 'url' | 'element' | 'text';
const previewType: PreviewType[] = ['page', 'url', 'element', 'text'];

export const useRandomPreviewSearch = () => {
  const fetchMetric = useMetricFetcher();
  const { namedElements } = useNamedElements();
  const { pages } = usePages();

  type RandomPreviewSearch = {
    primary: PrimaryValue | null;
    refinements: DependentEventFilter[];
  };

  const getRandomType = (): PreviewType => {
    const type = previewType[Math.floor(Math.random() * previewType.length)]!;
    if ((type == 'element' && namedElements.length == 0) || (type == 'page' && pages.size == 0)) {
      return getRandomType();
    }

    return type;
  };

  const getRandomPreviewSearch = useCallback(async (): Promise<RandomPreviewSearch> => {
    const randomPreviewType = getRandomType();
    if (randomPreviewType === 'page') {
      const values = Array.from(pages.values());
      const activePages = values.filter(page => !page?.metadata?.archived);
      const page = activePages[Math.floor(Math.random() * activePages.length)];

      return {
        primary: {
          type: ValueType.Page,
          value: {
            value: page?.id || 0,
            type: PageRefValueType.Id,
          },
        },
        refinements: [],
      };
    }

    if (randomPreviewType === 'element') {
      const activeElements = namedElements.filter(element => !element.metadata?.archived);
      const element = activeElements[Math.floor(Math.random() * activeElements.length)]!;
      return {
        primary: null,
        refinements: [
          {
            element: {
              withElementId: {
                value: [element.namedElement?.id || ''],
                spec: [],
              },
            },
          },
        ],
      };
    }

    if (randomPreviewType === 'text') {
      const event = { primary: null, deps: [] };
      const metric = getMetricForEvent({ event, property: 'text', limit: 50, noFallback: true });
      const result = await fetchMetric(metric);
      const texts = result[0]?.data?.table?.map(row => row.groups.map(g => g.groupId)).flat() || [];
      const randomText = texts[Math.floor(Math.random() * texts.length)]!;

      return {
        primary: null,
        refinements: [
          {
            text: {
              equals: {
                value: [randomText],
              },
            },
          },
        ],
      };
    }

    if (randomPreviewType === 'url') {
      const event = { primary: null, deps: [] };
      const metric = getMetricForEvent({ event, property: 'url', limit: 50, noFallback: true });
      const result = await fetchMetric(metric);
      const urls = result[0]?.data?.table?.map(row => row.groups.map(g => g.groupId)).flat() || [];
      const randomUrl = urls[Math.floor(Math.random() * urls.length)]!;

      return {
        primary: {
          type: ValueType.Url,
          value: randomUrl,
        },
        refinements: [],
      };
    }

    return { primary: null, refinements: [] };
  }, [fetchMetric, namedElements, pages]);

  return { getRandomPreviewSearch };
};
